import type { CreativeFamily } from './creative-api';

/** What the Website Creatives banners section shows. */
export type BannersView = 'skeleton' | 'error' | 'empty' | 'list';

/** The slice of the creatives state the banners section decides on. */
export interface BannersViewInput {
  loading: boolean;
  loadFailed: boolean;
  family: CreativeFamily | undefined;
  /** The owner has no brand to read for (the brand list answered with none, or could not be read). */
  noBrand?: boolean;
}

/**
 * Which state the banners section renders: the skeleton until a read of the brand's family has answered, an
 * error (with a retry) when the read failed, the empty copy only when a read answered with no placements, else
 * the banners. Pure, so the page and its specs share one rule.
 */
export const bannersViewOf = (state: BannersViewInput): BannersView => {
  // No brand will ever be read for: the empty copy, never a skeleton that does not end.
  if (state.noBrand) return 'empty';
  if (state.loading) return 'skeleton';
  if (state.loadFailed) return 'error';
  if ((state.family?.placements.length ?? 0) === 0) return 'empty';
  return 'list';
};

/**
 * Whether the sub-tab strip shows over the banners section: always once the banners are listed; while they are not
 * (still loading, the read failed, none yet) only when Above the fold, read on its own, is to be reached (it has
 * something to show, or its read failed and offers a retry: `aboveTheFoldReachable`), so its tab stays reachable and
 * the pool and the carousels show the banners' state. Pure.
 */
export const stripOverBannersState = (view: BannersView, aboveTheFoldReachable: boolean): boolean =>
  view === 'list' || aboveTheFoldReachable;
